'use client';

import * as React from 'react';
import {
  ArticleCard,
  type ArticleCardProps,
} from '@/hen-ui/composites/ArticleCard';
import { Button } from '@/hen-ui/atomics/Button';
import { Text } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';
import { Grid } from '@/hen-ui/composites/Grid';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';

/** Initial cards shown per parent hub section. */
export const CATEGORY_HUB_SECTION_PREVIEW_SIZE = 9;

/** Additional cards revealed each time Load more is clicked. */
export const CATEGORY_HUB_SECTION_LOAD_MORE_SIZE = 6;

export interface CategoryHubSectionProps {
  /** Heading slot — pass `[hen-ui-heading]` or `<Heading>`. May be empty; count stays in the header row. */
  heading?: React.ReactNode;
  /** DOM id for in-page jump links (typically the WP category slug). */
  sectionId: string;
  articles: ArticleCardProps[];
  /** Leaf category hub URL — used by View all only. */
  viewAllHref: string;
  previewSize?: number;
  loadMoreSize?: number;
  /**
   * When true, shows inline Load more and hides View all.
   * Default false — View all is the single overflow CTA when more posts exist.
   */
  showLoadMore?: boolean;
  /** Optional link primitive for View all (e.g. Next.js `Link`). */
  viewAllLinkComponent?: React.ElementType;
  className?: string;
}

function formatCountLabel(count: number): string {
  return count === 1 ? '(1)' : `(${count})`;
}

function slugToLabel(sectionId: string): string {
  return sectionId.replace(/-/g, ' ').trim() || 'this section';
}

/** Plain-text label from heading slot (string or `<Heading>text</Heading>`). */
function headingText(heading: React.ReactNode): string {
  if (typeof heading === 'string') return heading.trim();
  if (React.isValidElement<{ children?: React.ReactNode }>(heading)) {
    const { children } = heading.props;
    if (typeof children === 'string') return children.trim();
  }
  return '';
}

function prepareSectionHeading(
  heading: React.ReactNode,
  headingId: string,
): React.ReactNode {
  if (!React.isValidElement<{ id?: string; className?: string }>(heading)) {
    return heading ?? null;
  }
  return React.cloneElement(heading, {
    id: heading.props.id ?? headingId,
    className: cn('text-neutral-900', heading.props.className),
  });
}

/**
 * Parent hub section: heading slot + count, preview grid, and a single overflow
 * CTA — View all (navigates to the leaf category) by default, or inline Load more
 * when `showLoadMore` is true. Never both.
 */
export function CategoryHubSection({
  heading,
  sectionId,
  articles,
  viewAllHref,
  previewSize = CATEGORY_HUB_SECTION_PREVIEW_SIZE,
  loadMoreSize = CATEGORY_HUB_SECTION_LOAD_MORE_SIZE,
  showLoadMore = false,
  viewAllLinkComponent: ViewAllLink = 'a',
  className,
}: CategoryHubSectionProps) {
  const totalCount = articles.length;
  const [visibleCount, setVisibleCount] = React.useState(() =>
    Math.min(previewSize, totalCount),
  );

  React.useEffect(() => {
    setVisibleCount(Math.min(previewSize, totalCount));
  }, [sectionId, previewSize, totalCount]);

  const visibleArticles = articles.slice(0, visibleCount);
  const canLoadMore = visibleCount < totalCount;
  const hasOverflow = totalCount > previewSize;
  /** View all when overflow exists and Load more is not opted in. */
  const showViewAll = hasOverflow && !showLoadMore;

  const loadMore = React.useCallback(() => {
    setVisibleCount((current) => Math.min(totalCount, current + loadMoreSize));
  }, [loadMoreSize, totalCount]);

  const headingId = `${sectionId}-heading`;
  const title = headingText(heading);
  const sectionLabel = title || slugToLabel(sectionId);
  const headingNode = prepareSectionHeading(heading, headingId);

  const sectionProps = {
    id: sectionId,
    className: 'scroll-mt-24' as const,
    ...(title
      ? { 'aria-labelledby': headingId }
      : { 'aria-label': sectionLabel }),
  };

  if (totalCount === 0) {
    return (
      <section {...sectionProps}>
        <Flex direction='column' gap='md' className={className}>
          {headingNode}
          <Text variant='b3' color='subtext'>
            No articles in this section yet.
          </Text>
        </Flex>
      </section>
    );
  }

  return (
    <section {...sectionProps}>
      <Flex direction='column' gap='xl' className={className}>
        <Flex
          direction='row'
          justify='between'
          align='center'
          gap='md'
          className='w-full'
        >
          <Flex direction='row' justify='start' gap='xs' align='center'>
            {headingNode}
            <Text variant='b3' color='subtext'>
              {formatCountLabel(totalCount)}
            </Text>
          </Flex>
          {showViewAll && (
            <Button
              asChild
              variant='textOnly'
              size='sm'
              data-hen-id={COMPONENT_IDS.CategoryHubSection}
            >
              <ViewAllLink
                href={viewAllHref}
                aria-label={`View all articles in ${sectionLabel}`}
              >
                View all
              </ViewAllLink>
            </Button>
          )}
        </Flex>

        <Grid columns={1} columns-md={2} columns-lg={3} gap='md'>
          {visibleArticles.map((article, index) => (
            <ArticleCard key={article.href ?? index} {...article} />
          ))}
        </Grid>

        {showLoadMore && canLoadMore && (
          <Flex direction='row' justify='start' align='start'>
            <Button
              type='button'
              variant='blackOutline'
              size='sm'
              iconAfter='arrow-down'
              onClick={loadMore}
              aria-label={`Load more articles in ${sectionLabel} (${
                totalCount - visibleCount
              } remaining)`}
              data-hen-id={COMPONENT_IDS.CategoryHubSection}
            >
              Load more
            </Button>
          </Flex>
        )}
      </Flex>
    </section>
  );
}

CategoryHubSection.displayName = 'CategoryHubSection';
